import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Plus, UserPlus, IdentificationCard } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import {
  portalSubmitOrderDraft, portalListMyDrafts, portalListMyBeneficiaries,
  portalCreateBeneficiary, portalListCatalogBrands, portalListCatalogModels,
  portalListCatalogColors, portalCancelMyDraft,
} from '../../services/api';

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-700',
};

const emptyEndClient = {
  name: '', id_type: 'national_id', id_number: '',
  nin_number: '', passport_number: '', id_card_number: '',
  phone: '', email: '', address: '', date_of_birth: '',
  relationship: 'customer', notes: '',
};

const PortalNewOrder = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    brand: '', brand_id: '', model: '', model_id: '', color: '', quantity: 1, notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const [drafts, setDrafts] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [brands, setBrands] = useState([]);
  const [allModels, setAllModels] = useState([]);
  const [colors, setColors] = useState([]);

  // End-client picker
  const [endClientMode, setEndClientMode] = useState('existing'); // 'existing' | 'new'
  const [selectedEndClientId, setSelectedEndClientId] = useState('');
  const [newEndClient, setNewEndClient] = useState(emptyEndClient);

  const loadDrafts = async () => {
    try { const r = await portalListMyDrafts(); setDrafts(r.data || []); } catch { /* ignore */ }
  };
  const loadBeneficiaries = async () => {
    try { const r = await portalListMyBeneficiaries(); setBeneficiaries(r.data || []); } catch { /* ignore */ }
  };
  const loadCatalog = async () => {
    try {
      const [b, m, c] = await Promise.all([
        portalListCatalogBrands(),
        portalListCatalogModels(),
        portalListCatalogColors(),
      ]);
      setBrands(b.data || []);
      setAllModels(m.data || []);
      setColors(c.data?.colors || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadDrafts(); loadBeneficiaries(); loadCatalog(); }, []);

  // Auto-switch to "new" mode when there are no beneficiaries yet
  useEffect(() => {
    if (beneficiaries.length === 0) setEndClientMode('new');
  }, [beneficiaries.length]);

  const modelsForBrand = form.brand_id ? allModels.filter((m) => m.brand_id === form.brand_id) : [];

  const submit = async () => {
    if (!form.brand_id || !form.model_id) { toast.error('Please pick brand and model'); return; }
    if (!form.color) { toast.error('Please pick a color'); return; }

    // End-client validation
    let payload = { ...form, quantity: Number(form.quantity) || 1 };
    if (endClientMode === 'existing') {
      if (!selectedEndClientId) { toast.error('Please pick an end client or switch to "Create new"'); return; }
      payload.end_client_id = selectedEndClientId;
    } else {
      if (!newEndClient.name.trim()) { toast.error('End-client name is required'); return; }
      payload.new_end_client = newEndClient;
    }
    setSubmitting(true);
    try {
      await portalSubmitOrderDraft(payload);
      toast.success('Order request submitted — our team will review shortly.');
      setForm({ brand: '', brand_id: '', model: '', model_id: '', color: '', quantity: 1, notes: '' });
      setNewEndClient(emptyEndClient);
      setSelectedEndClientId('');
      loadDrafts();
      loadBeneficiaries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="portal-new-order-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Plus className="w-7 h-7" /> New Order Request</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Tell us what you need. Your request goes to our admin team. Once approved with a confirmed price,
          it appears as a real order under <button onClick={() => navigate('/portal/orders')} className="underline">My Orders</button>.
        </p>
      </div>

      <div className="border border-border p-5 bg-white space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Brand */}
          <div>
            <Label>Brand <span className="text-red-500">*</span></Label>
            <Select
              value={form.brand_id}
              onValueChange={(v) => {
                const b = brands.find((x) => x.id === v);
                setForm({ ...form, brand_id: v, brand: b?.name || '', model: '', model_id: '' });
              }}
            >
              <SelectTrigger data-testid="draft-brand-select"><SelectValue placeholder={brands.length ? 'Pick a brand…' : 'Loading catalog…'} /></SelectTrigger>
              <SelectContent className="max-h-72">
                {brands.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          {/* Model */}
          <div>
            <Label>Model <span className="text-red-500">*</span></Label>
            <Select
              value={form.model_id}
              onValueChange={(v) => {
                const m = allModels.find((x) => x.id === v);
                setForm({ ...form, model_id: v, model: m?.name || '' });
              }}
              disabled={!form.brand_id}
            >
              <SelectTrigger data-testid="draft-model-select">
                <SelectValue placeholder={!form.brand_id ? 'Pick a brand first' : modelsForBrand.length ? 'Pick a model…' : 'No models for this brand yet'} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {modelsForBrand.map((m) => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          {/* Color */}
          <div>
            <Label>Color <span className="text-red-500">*</span></Label>
            <Select value={form.color} onValueChange={(v) => setForm({ ...form, color: v })}>
              <SelectTrigger data-testid="draft-color-select"><SelectValue placeholder="Pick a color…" /></SelectTrigger>
              <SelectContent>
                {colors.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          {/* Quantity */}
          <div>
            <Label>Quantity</Label>
            <Input type="number" min="1" value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              data-testid="draft-quantity-input" />
          </div>
        </div>

        {/* End-client */}
        <div className="border-t border-border pt-4 space-y-2">
          <Label className="flex items-center gap-2"><IdentificationCard size={18} /> End client (final buyer of this car) <span className="text-red-500">*</span></Label>
          <p className="text-xs text-muted-foreground">
            Documents (NIN, passport, ID card) are stored on the <strong>end client's profile</strong>, not on your dealer profile.
            Pick an existing end-client or create a new one — they'll be saved to your list.
          </p>
          <div className="flex gap-2 mt-1">
            <Button
              size="sm" type="button"
              variant={endClientMode === 'existing' ? 'default' : 'outline'}
              onClick={() => setEndClientMode('existing')}
              disabled={beneficiaries.length === 0}
              data-testid="endclient-existing-tab"
            >
              From my end-clients ({beneficiaries.length})
            </Button>
            <Button
              size="sm" type="button"
              variant={endClientMode === 'new' ? 'default' : 'outline'}
              onClick={() => setEndClientMode('new')}
              data-testid="endclient-new-tab"
            >
              <UserPlus size={16} className="mr-1" /> Create new
            </Button>
          </div>

          {endClientMode === 'existing' ? (
            <Select value={selectedEndClientId} onValueChange={setSelectedEndClientId}>
              <SelectTrigger data-testid="endclient-select"><SelectValue placeholder="Pick one of your end-clients…" /></SelectTrigger>
              <SelectContent>
                {beneficiaries.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name}{b.nin_number ? ` · NIN ${b.nin_number.slice(-6)}` : ''}{b.phone ? ` · ${b.phone}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 border border-dashed border-border rounded-md bg-muted/20">
              <div className="sm:col-span-2">
                <Label>Full name <span className="text-red-500">*</span></Label>
                <Input value={newEndClient.name} onChange={(e) => setNewEndClient({ ...newEndClient, name: e.target.value })} data-testid="new-endclient-name" />
              </div>
              <div>
                <Label>NIN (National ID)</Label>
                <Input value={newEndClient.nin_number} onChange={(e) => setNewEndClient({ ...newEndClient, nin_number: e.target.value })} placeholder="18 digits" data-testid="new-endclient-nin" />
              </div>
              <div>
                <Label>Passport #</Label>
                <Input value={newEndClient.passport_number} onChange={(e) => setNewEndClient({ ...newEndClient, passport_number: e.target.value })} data-testid="new-endclient-passport" />
              </div>
              <div>
                <Label>ID Card #</Label>
                <Input value={newEndClient.id_card_number} onChange={(e) => setNewEndClient({ ...newEndClient, id_card_number: e.target.value })} data-testid="new-endclient-idcard" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={newEndClient.phone} onChange={(e) => setNewEndClient({ ...newEndClient, phone: e.target.value })} data-testid="new-endclient-phone" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={newEndClient.email} onChange={(e) => setNewEndClient({ ...newEndClient, email: e.target.value })} />
              </div>
              <div>
                <Label>Date of birth</Label>
                <Input type="date" value={newEndClient.date_of_birth} onChange={(e) => setNewEndClient({ ...newEndClient, date_of_birth: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <Label>Address</Label>
                <Input value={newEndClient.address} onChange={(e) => setNewEndClient({ ...newEndClient, address: e.target.value })} />
              </div>
            </div>
          )}
        </div>

        <div>
          <Label>Notes</Label>
          <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Options, accessories, special requests…" data-testid="draft-notes-input" />
        </div>

        <div className="flex justify-end">
          <Button onClick={submit} disabled={submitting} data-testid="submit-draft-btn">
            {submitting ? 'Submitting…' : 'Submit request'}
          </Button>
        </div>
      </div>

      <div>
        <h2 className="font-semibold text-lg mb-2">Your requests</h2>
        {drafts.length === 0 ? (
          <p className="text-sm text-muted-foreground">You haven't submitted any requests yet.</p>
        ) : (
          <div className="border border-border overflow-x-auto bg-white">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40">
                <tr>
                  <th className="text-left p-3">Submitted</th>
                  <th className="text-left p-3">Car</th>
                  <th className="text-center p-3">Qty</th>
                  <th className="text-left p-3">End client</th>
                  <th className="text-center p-3">Status</th>
                  <th className="text-left p-3">Result</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {drafts.map((d) => (
                  <tr key={d.id} className="border-t border-border">
                    <td className="p-3 text-xs">{fmt(d.created_at)}</td>
                    <td className="p-3">{d.brand} {d.model} <span className="text-muted-foreground">· {d.color || '—'}</span></td>
                    <td className="p-3 text-center">{d.quantity}</td>
                    <td className="p-3 text-xs">{d.end_client_name || '—'}</td>
                    <td className="p-3 text-center"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_BADGE[d.status] || ''}`}>{d.status}</span></td>
                    <td className="p-3 text-xs">
                      {d.status === 'approved' && d.linked_order_number ? (
                        <span className="text-emerald-700">
                          → Order <button onClick={() => navigate(`/portal/orders`)} className="underline font-medium">{d.linked_order_number}</button>
                          {d.approved_selling_price ? ` · ${d.approved_selling_price.toLocaleString()} DA` : ''}
                        </span>
                      ) : d.status === 'rejected' && d.rejection_reason ? (
                        <span className="text-red-700">{d.rejection_reason}</span>
                      ) : d.status === 'cancelled' && d.cancellation_reason ? (
                        <span className="text-gray-700">{d.cancellation_reason}</span>
                      ) : '—'}
                    </td>
                    <td className="p-3 text-right">
                      {d.status === 'pending' ? (
                        <Button
                          size="sm" variant="ghost" className="text-red-700"
                          onClick={async () => {
                            const reason = window.prompt('Cancel this request? Optional reason:');
                            if (reason === null) return;
                            try {
                              await portalCancelMyDraft(d.id, reason || '');
                              toast.success('Request cancelled');
                              loadDrafts();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Cancel failed');
                            }
                          }}
                          data-testid={`portal-cancel-draft-${d.id}`}
                        >
                          Cancel
                        </Button>
                      ) : d.status === 'approved' ? (
                        <span className="text-[11px] text-muted-foreground italic">Contact us to cancel</span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalNewOrder;
